import { useState, type FormEvent } from "react";

import {
  apiClient,
  type DepartmentDirectoryItem,
  type DepartmentImpact,
  type DepartmentSummary,
} from "../../api/client";
import { errorMessage } from "../admin/errors";
import { DepartmentLifecycleDialog } from "./DepartmentLifecycleDialog";
import { useModalFocusTrap } from "./useModalFocusTrap";

interface DepartmentDetailDrawerProps {
  department: DepartmentDirectoryItem;
  onChanged: (department: DepartmentSummary) => Promise<void> | void;
  onClose: () => void;
}

export function DepartmentDetailDrawer({
  department,
  onChanged,
  onClose,
}: DepartmentDetailDrawerProps) {
  const [name, setName] = useState(department.name);
  const [busy, setBusy] = useState(false);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [action, setAction] = useState<"deactivate" | "reactivate" | null>(null);
  const [impact, setImpact] = useState<DepartmentImpact | null>(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const drawer = useModalFocusTrap<HTMLElement>("[data-initial-focus]");

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!name.trim() || name.trim() === department.name) return;
    await perform(async () => {
      const updated = await apiClient.updateDepartment(department.id, name.trim());
      setName(updated.name);
      await onChanged(updated);
      setMessage("部门名称已更新，稳定编码和历史引用保持不变。");
    });
  }

  async function prepareDeactivation() {
    setLoadingImpact(true);
    setError("");
    setMessage("");
    try {
      setImpact(await apiClient.getDepartmentDeactivationImpact(department.id));
      setAction("deactivate");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoadingImpact(false);
    }
  }

  async function confirmLifecycle() {
    if (!action) return;
    await perform(async () => {
      const updated =
        action === "deactivate"
          ? await apiClient.deactivateDepartment(department.id)
          : await apiClient.reactivateDepartment(department.id);
      await onChanged(updated);
      setAction(null);
      setImpact(null);
      setMessage(
        action === "deactivate"
          ? "部门已停用，活动授权与配额已清理。"
          : "部门已恢复；授权与配额仍需重新配置。",
      );
    });
  }

  async function perform(operation: () => Promise<void>) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await operation();
    } catch (caught) {
      setAction(null);
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="drawer-backdrop" role="presentation">
      <aside
        ref={drawer}
        aria-labelledby="department-detail-title"
        aria-modal="true"
        className="user-detail-drawer department-detail-drawer"
        onKeyDown={(event) => {
          if (event.key === "Escape" && !busy && !action) onClose();
        }}
        role="dialog"
        tabIndex={-1}
      >
        <header className="user-detail-header">
          <div>
            <span className="eyebrow">部门详情</span>
            <h2 id="department-detail-title">{department.name}</h2>
            <p>{department.status === "active" ? "活动" : "已停用"}</p>
          </div>
          <button
            data-initial-focus
            aria-label="关闭部门详情"
            className="text-button"
            disabled={busy}
            onClick={onClose}
            type="button"
          >
            关闭
          </button>
        </header>
        <div className="user-detail-sections">
          <section className="user-detail-section">
            <div>
              <span className="eyebrow">资料</span>
              <h3>名称与稳定编码</h3>
            </div>
            <form className="department-form" onSubmit={(event) => void save(event)}>
              <label>
                部门名称
                <input
                  maxLength={160}
                  onChange={(event) => setName(event.target.value)}
                  required
                  value={name}
                />
              </label>
              <label>
                部门编码（不可修改）
                <input disabled readOnly value={department.code} />
              </label>
              <button disabled={busy || !name.trim() || name.trim() === department.name}>
                {busy ? "正在保存…" : "保存名称"}
              </button>
            </form>
          </section>
          <section className="user-detail-section department-impact-section">
            <div>
              <span className="eyebrow">当前影响</span>
              <h3>活动关联</h3>
            </div>
            <dl className="department-impact-grid">
              <div>
                <dt>活动用户</dt>
                <dd>{department.active_user_count}</dd>
              </div>
              <div>
                <dt>活动授权</dt>
                <dd>{department.active_grant_count}</dd>
              </div>
              <div>
                <dt>活动配额</dt>
                <dd>{department.active_quota_count}</dd>
              </div>
            </dl>
          </section>
          <section className="user-detail-section danger-zone">
            <div>
              <span className="eyebrow">生命周期</span>
              <h3>{department.status === "active" ? "停用部门" : "恢复部门"}</h3>
            </div>
            <p>停用不会删除部门或历史记录；恢复也不会静默恢复此前清理的访问配置。</p>
            <button
              disabled={busy || loadingImpact}
              onClick={() => {
                if (department.status === "active") void prepareDeactivation();
                else setAction("reactivate");
              }}
              type="button"
            >
              {loadingImpact
                ? "正在读取影响…"
                : department.status === "active"
                  ? "查看影响并停用"
                  : "恢复部门"}
            </button>
          </section>
          {message && (
            <p className="status-banner" role="status">
              {message}
            </p>
          )}
          {error && (
            <p className="alert error" role="alert">
              {error}
            </p>
          )}
        </div>
        {action && (
          <DepartmentLifecycleDialog
            action={action}
            busy={busy}
            impact={impact}
            name={department.name}
            onCancel={() => {
              setAction(null);
              setImpact(null);
            }}
            onConfirm={() => void confirmLifecycle()}
          />
        )}
      </aside>
    </div>
  );
}
